import { useCallback } from 'react';
import * as Y from 'yjs';
import { notification } from '@nula/core-ui';
import { canvasRoots } from '@/features/canvas/engine/wiring/canvas-roots';
import { useCanvasBinding } from '@/features/canvas/engine/wiring/contexts/canvas-binding-context';
import { resolveEntities } from '../api/resolve-entities';
import {
  makeEntityProxyData,
  makeTextNodeData,
} from '@/features/canvas/engine/schema/defaults';

export interface UseResolverOnPasteArgs {
  doc: Y.Doc;
  clientId: number;
  workspaceId: string;
}

/**
 * Returns a stable `handlePaste(text, position)` callback that:
 *   1. Splits text on newlines, trims, filters empty lines.
 *   2. Calls the S7 resolver endpoint for each non-empty line.
 *   3. Dispatches results into Yjs nodes in a single transact:
 *      - matched → entity-proxy (isVirtual=false, tentative=false)
 *      - ambiguous → entity-proxy with top candidate (tentative=true)
 *      - no-match → plain text node (never a virtual proxy — those
 *        come from agent emission only, per client.md §14).
 *   4. On resolver error: fires notification.error, creates no nodes.
 *
 * All Yjs writes batched in one doc.transact(..., clientId) so they
 * merge into a single undo step and one WS update event.
 */
export function useResolverOnPaste({
  doc,
  clientId,
  workspaceId,
}: UseResolverOnPasteArgs) {
  const binding = useCanvasBinding();
  return useCallback(
    async (text: string, position: { x: number; y: number }) => {
      if (!workspaceId) return;
      const lines = text
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter((l) => l.length > 0);
      if (lines.length === 0) return;

      const queries = lines.map((title, i) => ({ id: String(i), title }));
      let resolutions;
      try {
        resolutions = await resolveEntities(workspaceId, queries);
      } catch (err) {
        notification.error({
          message:
            err instanceof Error
              ? err.message
              : 'Could not resolve pasted text. Try again.',
        });
        return; // No nodes created on resolver failure.
      }

      doc.transact(() => {
        const nodes = canvasRoots(doc, binding).nodes;
        resolutions.forEach((r, i) => {
          const id = crypto.randomUUID();
          const node = new Y.Map();
          node.set('id', id);
          node.set('position', { x: position.x, y: position.y + i * 80 });

          if (r.outcome === 'matched') {
            node.set('type', 'entity-proxy');
            const data = new Y.Map();
            const proxyData = makeEntityProxyData({
              entityId: r.entityId,
              entityKind: r.kind,
              isVirtual: false,
              tentative: false,
            });
            Object.entries(proxyData).forEach(([k, v]) => data.set(k, v));
            node.set('data', data);
          } else if (r.outcome === 'ambiguous' && r.candidates.length > 0) {
            const top = r.candidates[0];
            node.set('type', 'entity-proxy');
            const data = new Y.Map();
            const proxyData = makeEntityProxyData({
              entityId: top.entityId,
              entityKind: top.kind,
              isVirtual: false,
              tentative: true,
            });
            Object.entries(proxyData).forEach(([k, v]) => data.set(k, v));
            node.set('data', data);
          } else {
            // no-match: plain text node
            node.set('type', 'text');
            const data = new Y.Map();
            const textData = makeTextNodeData({ text: lines[i] });
            Object.entries(textData).forEach(([k, v]) => data.set(k, v));
            node.set('data', data);
          }

          nodes.set(id, node);
        });
      }, clientId);
    },
    [doc, clientId, workspaceId, binding],
  );
}
